// The HTML constraints a zod schema implies, per field.
// Adapted from @conform-to/zod 1.19.4 `getZodConstraint` (MIT). Its walker
// never needed zod; importing it from Conform's `v4/future` entry does, from
// Conform 1.20.
import { type useForm } from '@conform-to/react';
import { type $ZodType } from 'zod/v4/core';

import {
  formatPaths,
  getPaths,
  getRelativePath,
  type PathSegment,
} from './paths';

// Conform's per-field constraint, which @conform-to/react does not export.
type Constraint = NonNullable<
  NonNullable<Parameters<typeof useForm>[0]>['constraint']
>[string];
type Data = Record<string, Constraint>;
type Def = Record<string, unknown> & { type: string };
type Bag = {
  minimum?: number;
  maximum?: number;
  patterns?: Set<RegExp>;
  mime?: string[];
};

interface Node {
  type: $ZodType;
  def: Def;
  bag: Bag;
  data: Data;
  name: string;
  constraint: Constraint;
  update: (type: $ZodType, data: Data, name: string) => void;
}
type Handler = (node: Node) => void;

const KEYS = [
  'required',
  'minLength',
  'maxLength',
  'min',
  'max',
  'step',
  'multiple',
  'pattern',
  'accept',
] as const;

// One HTML pattern that every regex must match, or none when that cannot be
// written (a case-insensitive regex, or one the `v` flag rejects).
const serializeHtmlPattern = (patterns: RegExp[]) => {
  if (patterns.length === 0 || patterns.some((p) => p.flags.includes('i'))) {
    return undefined;
  }
  const pattern = `^${patterns.map((p) => `(?=.*(?:${p.source}))`).join('')}.*$`;
  try {
    new RegExp(pattern, 'v');
  } catch {
    return undefined;
  }
  return pattern;
};

const child = (node: Node, key: string) => node.def[key] as $ZodType;

// Whether a missing value is valid. The parser fills a missing value for
// these, so the browser must not require one.
const REQUIRED: Record<string, boolean | undefined> = {
  optional: false,
  default: false,
  prefault: false,
  catch: false,
  nonoptional: true,
};

// A wrapper constrains its field like the schema inside it.
const unwrap: Handler = (node) => {
  node.update(child(node, 'innerType'), node.data, node.name);
  const required = REQUIRED[node.def.type];
  // Set after the inner schema, which can replace the field's entry.
  if (required !== undefined && node.name !== '') {
    node.data[node.name] = { ...node.data[node.name], required };
  }
};

// Both stages validate the field, so both constrain it, and it is required
// when any stage requires it (unless a wrapper outside already made it optional).
const both = (keys: string[]): Handler => {
  return (node) => {
    const { data, name } = node;
    const outer = name === '' || data[name]?.required !== false;
    let required = false;
    for (const key of keys) {
      // A transform validates nothing, so it has no say in `required`.
      if (child(node, key)._zod.def.type === 'transform') continue;
      if (name !== '') data[name] = { ...data[name], required: true };
      node.update(child(node, key), data, name);
      required ||= name !== '' && data[name]?.required === true;
    }
    if (name !== '')
      data[name] = { ...data[name], required: outer && required };
  };
};

// A field is only as constrained as every option agrees.
const mergeOptions = (prev: Data, next: Data): Data => {
  const merged: Data = {};
  for (const field of new Set([...Object.keys(prev), ...Object.keys(next)])) {
    const a = prev[field];
    const b = next[field];
    if (a && b) {
      const shared: Record<string, unknown> = {};
      for (const key of KEYS) {
        if (a[key] !== undefined && a[key] === b[key]) shared[key] = a[key];
      }
      merged[field] = shared as Constraint;
    } else {
      merged[field] = { ...a, ...b, required: false };
    }
  }
  return merged;
};

// Schemas that carry no constraint of their own, so they work at the root.
const STRUCTURAL: Record<string, Handler> = {
  object: (node) => {
    for (const [key, value] of Object.entries(
      node.def['shape'] as Record<string, $ZodType>,
    )) {
      node.update(value, node.data, node.name ? `${node.name}.${key}` : key);
    }
  },
  union: (node) => {
    const options = (node.def['options'] as $ZodType[]).map((option) => {
      const optionData: Data = {};
      node.update(option, optionData, node.name);
      return optionData;
    });
    Object.assign(node.data, options.reduce(mergeOptions));
  },
  lazy: (node) =>
    node.update((node.def['getter'] as () => $ZodType)(), node.data, node.name),
  pipe: both(['in', 'out']),
  intersection: both(['left', 'right']),
  transform: () => undefined,
  optional: unwrap,
  nullable: unwrap,
  default: unwrap,
  prefault: unwrap,
  catch: unwrap,
  nonoptional: unwrap,
};

const FIELD: Record<string, Handler> = {
  array: (node) => {
    node.constraint.multiple = true;
    node.update(child(node, 'element'), node.data, `${node.name}[]`);
  },
  string: ({ bag, constraint }) => {
    if (bag.minimum != null) constraint.minLength = bag.minimum;
    if (bag.maximum != null) constraint.maxLength = bag.maximum;
    if (bag.patterns?.size) {
      const pattern = serializeHtmlPattern([...bag.patterns]);
      if (pattern) constraint.pattern = pattern;
    }
  },
  number: ({ bag, constraint }) => {
    if (bag.minimum != null) constraint.min = bag.minimum;
    if (bag.maximum != null) constraint.max = bag.maximum;
  },
  // The accepted values, which differ from the keys in z.enum({ Draft: 'draft' }).
  enum: ({ type, constraint }) => {
    constraint.pattern = [...(type._zod.values ?? [])]
      .map((option) =>
        String(option)
          .replace(/[|\\{}()[\]^$+*?.]/g, '\\$&')
          .replace(/-/g, '\\x2d'),
      )
      .join('|');
  },
  tuple: (node) => {
    (node.def['items'] as $ZodType[]).forEach((item, index) =>
      node.update(item, node.data, `${node.name}[${index}]`),
    );
  },
  file: ({ bag, constraint }) => {
    if (bag.mime) constraint.accept = bag.mime.join();
  },
};

/**
 * The field constraints (`required`, `minLength`, `maxLength`, `pattern`, ...)
 * a zod schema implies, keyed by field name, for Conform's `constraint`
 * option. Works with schemas built with `zod/mini` and with classic `zod`.
 *
 * @param schema The object schema the form validates against.
 * @returns One constraint object per field.
 */
export const getZodConstraint = (schema: $ZodType): Data => {
  const processing = new Map<$ZodType, string>();
  const aliases: { from: PathSegment[]; to: PathSegment[] }[] = [];
  const result: Data = {};
  const cache: Record<string, Constraint | undefined> = {};

  const update = (type: $ZodType, data: Data, name: string): void => {
    const seen = processing.get(type);
    if (seen !== undefined) {
      aliases.push({ from: getPaths(name), to: getPaths(seen) });
      return;
    }
    processing.set(type, name);
    const node: Node = {
      type,
      def: type._zod.def as unknown as Def,
      bag: type._zod.bag as Bag,
      data,
      name,
      constraint: name === '' ? {} : (data[name] ??= { required: true }),
      update,
    };
    const structural = STRUCTURAL[node.def.type];
    if (structural) {
      structural(node);
    } else if (name === '') {
      throw new Error('Unsupported schema');
    } else {
      FIELD[node.def.type]?.(node);
    }
    processing.delete(type);
  };

  const resolve = (path: string | PathSegment[]): Constraint | undefined => {
    const name = typeof path === 'string' ? path : formatPaths(path);
    if (name in result) return result[name];
    const segments = typeof path === 'string' ? getPaths(path) : [...path];
    for (const alias of aliases) {
      const tail = getRelativePath(segments, alias.from);
      if (tail !== null && tail.length > 0)
        return resolve([...alias.to, ...tail]);
    }
    for (let index = segments.length - 1; index >= 0; index--) {
      if (typeof segments[index] === 'number') {
        segments[index] = '';
        return resolve(segments);
      }
    }
    return undefined;
  };

  update(schema, result, '');
  return new Proxy(result, {
    get(target, name, receiver) {
      if (typeof name !== 'string') return Reflect.get(target, name, receiver);
      if (name in cache) return cache[name];
      return (cache[name] = resolve(name));
    },
  });
};
